import { useState } from "react";
import { X, Download } from "lucide-react";
import { JWT } from "../../config/constants";
import { API_BASE_URL } from "../../api/apiClient";
import { showError, showSuccess } from "../../utils/toast";

export default function ExportExcelModal({ isOpen, onClose, projects, isPublic = false, exportType = "lessons" }) {
  const isVaVe = exportType === "vave";
  const label = isVaVe ? "VA/VE" : "Lessons";
  const [selectedProject, setSelectedProject] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleExport() {
    if (!selectedProject) {
      showError("Pilih project terlebih dahulu");
      return;
    }

    setLoading(true);
    try {
      const basePath = isVaVe ? "vave" : "lessons";
      const url = isPublic
        ? `${API_BASE_URL}/api/public/${basePath}/export/excel?project_id=${selectedProject}`
        : `${API_BASE_URL}/api/${basePath}/export/excel?project_id=${selectedProject}`;

      const headers = {};
      if (!isPublic) {
        const token = localStorage.getItem(JWT.TOKEN_KEY);
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Export gagal");
      }

      // Get filename from header
      const disposition = response.headers.get("Content-Disposition");
      let filename = isVaVe ? "VaVeExport.xlsx" : "LessonsLearned.xlsx";
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

      showSuccess("Excel berhasil didownload");
      onClose();
    } catch (err) {
      console.error("Export error:", err);
      showError(err.message || "Gagal export data ke Excel");
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Export {label} ke Excel</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pilih Project
          </label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            <option value="">-- Pilih Project --</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.division ? `${project.name} (${project.division})` : project.name}
              </option>
            ))}
          </select>

          <p className="mt-3 text-sm text-gray-500">
            File Excel akan berisi header dengan logo client (jika sudah
            diupload), nama project, dan semua data {label.toLowerCase()} dari project yang
            dipilih.
          </p>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Batal
          </button>
          <button
            onClick={handleExport}
            disabled={loading || !selectedProject}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Download size={18} />
            <span>{loading ? "Exporting..." : "Export"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
